type BrandImages = {
  light?: string;
  dark?: string;
};

type StoreBrandConfig = {
  logo?: BrandImages;
  icon?: BrandImages;
  stores?: Array<{ icon?: BrandImages }>;
};

const pickUrl = (...candidates: (string | undefined | null)[]): string | undefined => {
  for (const url of candidates) {
    if (typeof url === 'string' && url.trim().length > 0) {
      return url.trim();
    }
  }
  return undefined;
};

function normalizeBrandImages(value: unknown): BrandImages | undefined {
  if (!value) return undefined;

  if (typeof value === 'string') {
    const url = value.trim();
    return url ? { light: url, dark: url } : undefined;
  }

  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const light = typeof source.light === 'string' ? source.light.trim() : '';
    const dark = typeof source.dark === 'string' ? source.dark.trim() : '';

    if (!light && !dark) return undefined;

    return {
      light: light || undefined,
      dark: dark || undefined,
    };
  }

  return undefined;
}

function resolveStoreIcons(
  config?: StoreBrandConfig | null,
  store?: { icon?: BrandImages } | null
) {
  const configIcon = normalizeBrandImages(config?.icon);
  const storeIcon =
    normalizeBrandImages(store?.icon) ??
    normalizeBrandImages(config?.stores?.[0]?.icon);

  return { configIcon, storeIcon };
}

/** Candidatos de ícone para o footer — store primeiro, depois config; light antes de dark */
export function getFooterIconCandidates(
  config?: StoreBrandConfig | null,
  store?: { icon?: BrandImages } | null
): string[] {
  const { configIcon, storeIcon } = resolveStoreIcons(config, store);

  const ordered = [
    storeIcon?.light,
    storeIcon?.dark,
    configIcon?.light,
    configIcon?.dark,
  ];

  return ordered
    .filter((url): url is string => typeof url === 'string' && url.length > 0)
    .filter((url, index, list) => list.indexOf(url) === index);
}

export function getStoreBrandAssets(
  config?: StoreBrandConfig | null,
  store?: { icon?: BrandImages } | null
) {
  const { configIcon, storeIcon } = resolveStoreIcons(config, store);

  const logoSrc = pickUrl(config?.logo?.dark, config?.logo?.light);
  const iconSrc = pickUrl(
    storeIcon?.dark,
    storeIcon?.light,
    configIcon?.dark,
    configIcon?.light
  );
  const footerIconCandidates = getFooterIconCandidates(config, store);
  const footerIconSrc = footerIconCandidates[0];

  const hasLogo = !!logoSrc;
  const headerBrandSrc = hasLogo ? logoSrc : iconSrc;

  return { hasLogo, logoSrc, iconSrc, footerIconSrc, footerIconCandidates, headerBrandSrc };
}
